
import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/base/Button';
import Input from '../../components/base/Input';
import Select from '../../components/base/Select';
import Modal from '../../components/base/Modal';

interface Password {
  id: string;
  title: string;
  website: string;
  username: string;
  password: string;
  email: string;
  kategorie: string;
  kontakt_id?: string;
  kontakt_name?: string;
  notizen: string;
  file_name?: string;
  file_url?: string;
  file_size?: number;
  last_used?: string;
  created_at: string;
  updated_at?: string;
}

interface Kontakt {
  id: string;
  name: string;
  firma: string;
  email: string;
}

export default function Passwoerter() {
  const { user } = useAuth();
  const [passwords, setPasswords] = useState<Password[]>([]);
  const [contacts, setContacts] = useState<Kontakt[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingPassword, setEditingPassword] = useState<Password | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [showPasswords, setShowPasswords] = useState<Record<string, boolean>>({});
  const [isGeneratorOpen, setIsGeneratorOpen] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState('');

  const [formData, setFormData] = useState({
    title: '',
    website: '',
    username: '',
    password: '',
    email: '',
    kategorie: '',
    kontakt_id: '',
    notizen: '',
    file: null as File | null
  });

  const categories = [
    'Website',
    'E-Mail',
    'Banking',
    'Social Media',
    'Shopping',
    'Arbeit',
    'Software',
    'Server/Hosting',
    'Router/Netzwerk',
    'Sonstige'
  ];

  useEffect(() => {
    if (user) {
      loadPasswords();
      loadContacts();
    }
  }, [user]);

  const loadPasswords = async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('passwords')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setPasswords(data || []);
    } catch (error) {
      console.error('Error loading passwords:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadContacts = async () => {
    if (!user?.id) return;

    try {
      const { data, error } = await supabase
        .from('daten')
        .select('*')
        .eq('user_id', user.id)
        .eq('data_type', 'contact')
        .order('data->name', { ascending: true });

      if (error) throw error;

      const contactData = data.map(item => ({
        id: item.id,
        name: item.data.name,
        firma: item.data.firma || '',
        email: item.data.email || ''
      }));

      setContacts(contactData);
    } catch (error) {
      console.error('Error loading contacts:', error);
    }
  };

  // Upload file to Supabase Storage "Passwort" bucket
  const uploadFile = async (file: File): Promise<{ url: string; error?: any }> => {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${user?.id}/${Date.now()}.${fileExt}`;

      const { data, error } = await supabase.storage
        .from('passwort')
        .upload(fileName, file);

      if (error) {
        console.error('Upload error:', error);
        return { url: '', error };
      }

      const { data: { publicUrl } } = supabase.storage
        .from('passwort')
        .getPublicUrl(fileName);

      return { url: publicUrl };
    } catch (error) {
      console.error('Upload error:', error);
      return { url: '', error };
    }
  };

  // Download file from Supabase Storage
  const downloadFile = async (fileUrl: string, fileName: string) => {
    try {
      const response = await fetch(fileUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download error:', error);
    }
  };

  const generatePassword = (length: number = 16, includeSymbols: boolean = true) => {
    const lowercase = 'abcdefghijklmnopqrstuvwxyz';
    const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const numbers = '0123456789';
    const symbols = '!@#$%^&*()_+-=[]{}|;:,.<>?';
    
    let chars = lowercase + uppercase + numbers;
    if (includeSymbols) {
      chars += symbols;
    }
    
    let password = '';
    for (let i = 0; i < length; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    
    return password;
  };

  const handleGeneratePassword = () => {
    const newPassword = generatePassword(16, true);
    setGeneratedPassword(newPassword);
    setFormData(prev => ({ ...prev, password: newPassword }));
  };

  const copyToClipboard = async (text: string, type: string) => {
    try {
      await navigator.clipboard.writeText(text);
      // Hier könnte ein Toast/Benachrichtigung hinzugefügt werden
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const togglePasswordVisibility = (passwordId: string) => {
    setShowPasswords(prev => ({
      ...prev,
      [passwordId]: !prev[passwordId]
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setFormData(prev => ({ ...prev, file }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user?.id) return;

    setUploading(true);

    try {
      const selectedContact = contacts.find(c => c.id === formData.kontakt_id);
      
      let fileUrl = null;
      let fileName = null;
      let fileSize = null;

      // Upload file if provided
      if (formData.file) {
        const { url, error } = await uploadFile(formData.file);
        if (error) {
          console.error('Error uploading file:', error);
          setUploading(false);
          return;
        }
        fileUrl = url;
        fileName = formData.file.name;
        fileSize = formData.file.size;
      }
      
      const passwordData = {
        user_id: user.id,
        title: formData.title,
        website: formData.website,
        username: formData.username,
        password: formData.password,
        email: formData.email,
        kategorie: formData.kategorie,
        kontakt_id: formData.kontakt_id || null,
        kontakt_name: selectedContact ? selectedContact.name : null,
        notizen: formData.notizen,
        file_name: fileName,
        file_url: fileUrl,
        file_size: fileSize
      };

      const { data, error } = await supabase
        .from('passwords')
        .insert(passwordData)
        .select()
        .single();

      if (error) throw error;

      setPasswords(prev => [data, ...prev]);

      resetForm();
      setIsModalOpen(false);
    } catch (error) {
      console.error('Error adding password:', error);
    } finally {
      setUploading(false);
    }
  };

  const openEditModal = (password: Password) => {
    setEditingPassword(password);
    setFormData({
      title: password.title,
      website: password.website || '',
      username: password.username || '',
      password: password.password,
      email: password.email || '',
      kategorie: password.kategorie || '',
      kontakt_id: password.kontakt_id || '',
      notizen: password.notizen || '',
      file: null
    });
    setIsEditModalOpen(true);
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user?.id || !editingPassword) return;

    setUploading(true);

    try {
      const selectedContact = contacts.find(c => c.id === formData.kontakt_id);
      
      let fileUrl = editingPassword.file_url;
      let fileName = editingPassword.file_name;
      let fileSize = editingPassword.file_size;

      // Upload new file if provided
      if (formData.file) {
        const { url, error } = await uploadFile(formData.file);
        if (error) {
          console.error('Error uploading file:', error);
          setUploading(false);
          return;
        }
        fileUrl = url;
        fileName = formData.file.name;
        fileSize = formData.file.size;
      }
      
      const passwordData = {
        title: formData.title,
        website: formData.website,
        username: formData.username,
        password: formData.password,
        email: formData.email,
        kategorie: formData.kategorie,
        kontakt_id: formData.kontakt_id || null,
        kontakt_name: selectedContact ? selectedContact.name : null,
        notizen: formData.notizen,
        file_name: fileName,
        file_url: fileUrl,
        file_size: fileSize,
        updated_at: new Date().toISOString()
      };

      const { data, error } = await supabase
        .from('passwords')
        .update(passwordData)
        .eq('id', editingPassword.id)
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) throw error;

      setPasswords(prev => prev.map(password => 
        password.id === editingPassword.id ? data : password
      ));

      resetForm();
      setEditingPassword(null);
      setIsEditModalOpen(false);
    } catch (error) {
      console.error('Error updating password:', error);
    } finally {
      setUploading(false);
    }
  };

  const deletePassword = async (id: string) => {
    if (!user?.id) return;

    if (!confirm('Sind Sie sicher, dass Sie dieses Passwort löschen möchten?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('passwords')
        .delete()
        .eq('id', id)
        .eq('user_id', user.id);

      if (error) throw error;

      setPasswords(prev => prev.filter(password => password.id !== id));
    } catch (error) {
      console.error('Error deleting password:', error);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const resetForm = () => {
    setFormData({
      title: '',
      website: '',
      username: '',
      password: '',
      email: '',
      kategorie: '',
      kontakt_id: '',
      notizen: '',
      file: null
    });
  };

  const getCategoryIcon = (category: string) => {
    const icons: Record<string, string> = {
      'Website': 'ri-global-line',
      'E-Mail': 'ri-mail-line',
      'Banking': 'ri-bank-line',
      'Social Media': 'ri-share-line',
      'Shopping': 'ri-shopping-cart-line',
      'Arbeit': 'ri-briefcase-line',
      'Software': 'ri-code-line',
      'Server/Hosting': 'ri-server-line',
      'Router/Netzwerk': 'ri-router-line',
      'Sonstige': 'ri-file-line'
    };
    return icons[category] || 'ri-lock-line';
  };

  const filteredPasswords = passwords.filter(password => {
    const matchesSearch = password.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         password.website?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         password.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         password.kontakt_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !categoryFilter || password.kategorie === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-line text-4xl text-blue-600 animate-spin mb-4"></i>
          <p className="text-gray-600">Passwörter werden geladen...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full overflow-x-hidden">
      <div className="w-full max-w-full overflow-x-hidden">
        <div className="bg-white rounded-lg shadow-sm border w-full overflow-x-hidden">
          <div className="px-3 sm:px-4 lg:px-6 py-4 border-b border-gray-200">
            <div className="flex flex-col space-y-3 sm:space-y-0 sm:flex-row sm:justify-between sm:items-center">
              <div className="min-w-0 flex-1">
                <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 flex items-center">
                  <i className="ri-lock-line mr-2"></i>
                  Passwörter
                  <span className="ml-3 text-sm text-blue-600">
                    <i className="ri-cloud-line mr-1"></i>
                    Supabase Storage
                  </span>
                </h1>
                <p className="text-gray-600 mt-1 text-sm sm:text-base break-words">
                  Sichere Passwortverwaltung mit Datei-Upload ({passwords.length} Passwörter)
                </p>
              </div>
              <button
                onClick={() => {
                  resetForm();
                  setIsModalOpen(true);
                }}
                className="w-full sm:w-auto bg-blue-600 text-white px-4 py-3 sm:py-2 rounded-md hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center flex-shrink-0"
              >
                <i className="ri-add-line w-4 h-4 flex items-center justify-center mr-2"></i>
                Passwort hinzufügen
              </button>
            </div>
          </div>

          {/* Filter und Suche */}
          {passwords.length > 0 && (
            <div className="px-3 sm:px-4 lg:px-6 py-4 border-b border-gray-200 bg-gray-50">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <i className="ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                    <input
                      type="text"
                      placeholder="Passwörter durchsuchen..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
                <div className="sm:w-48">
                  <Select
                    options={[
                      { value: '', label: 'Alle Kategorien' },
                      ...categories.map(cat => ({ value: cat, label: cat }))
                    ]}
                    value={categoryFilter}
                    onChange={(value) => setCategoryFilter(value)}
                  />
                </div>
              </div>
            </div>
          )}

          <div className="p-3 sm:p-4 lg:p-6 w-full overflow-x-hidden">
            {passwords.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-gray-400 mb-4">
                  <i className="ri-lock-line text-6xl w-16 h-16 flex items-center justify-center mx-auto"></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-700 mb-2">
                  Keine Passwörter vorhanden
                </h3>
                <p className="text-gray-500">
                  Fügen Sie Ihr erstes Passwort hinzu, um zu beginnen.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg-grid-cols-3 gap-6">
                {filteredPasswords.map((password) => (
                  <div key={password.id} className="bg-white rounded-lg shadow-sm border p-6 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center">
                        <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                          <i className={`${getCategoryIcon(password.kategorie)} text-blue-600 text-lg`}></i>
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900">{password.title}</h3>
                          {password.website && (
                            <p className="text-sm text-gray-600">{password.website}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => openEditModal(password)}
                          className="text-gray-400 hover:text-blue-600 cursor-pointer"
                          title="Bearbeiten"
                        >
                          <i className="ri-edit-line"></i>
                        </button>
                        <button
                          onClick={() => deletePassword(password.id)}
                          className="text-gray-400 hover:text-red-600 cursor-pointer"
                          title="Löschen"
                        >
                          <i className="ri-delete-bin-line"></i>
                        </button>
                      </div>
                    </div>

                    <div className="space-y-3">
                      {password.username && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Benutzername:</span>
                          <div className="flex items-center space-x-2">
                            <span className="text-sm font-medium">{password.username}</span>
                            <button
                              onClick={() => copyToClipboard(password.username, 'Benutzername')}
                              className="text-gray-400 hover:text-blue-600 cursor-pointer"
                            >
                              <i className="ri-clipboard-line"></i>
                            </button>
                          </div>
                        </div>
                      )}
                      
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Passwort:</span>
                        <div className="flex items-center space-x-2">
                          <span className="text-sm font-medium">
                            {showPasswords[password.id] ? password.password : '••••••••'}
                          </span>
                          <button
                            onClick={() => togglePasswordVisibility(password.id)}
                            className="text-gray-400 hover:text-blue-600 cursor-pointer"
                          >
                            <i className={showPasswords[password.id] ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                          </button>
                          <button
                            onClick={() => copyToClipboard(password.password, 'Passwort')}
                            className="text-gray-400 hover:text-blue-600 cursor-pointer"
                          >
                            <i className="ri-clipboard-line"></i>
                          </button>
                        </div>
                      </div>

                      {password.email && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">E-Mail:</span>
                          <div className="flex items-center space-x-2">
                            <span className="text-sm font-medium">{password.email}</span>
                            <button
                              onClick={() => copyToClipboard(password.email, 'E-Mail')}
                              className="text-gray-400 hover:text-blue-600 cursor-pointer"
                            >
                              <i className="ri-clipboard-line"></i>
                            </button>
                          </div>
                        </div>
                      )}

                      {password.file_name && password.file_url && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Datei:</span>
                          <div className="flex items-center space-x-2">
                            <span className="text-sm font-medium truncate max-w-32" title={password.file_name}>
                              {password.file_name}
                            </span>
                            <button
                              onClick={() => downloadFile(password.file_url!, password.file_name!)}
                              className="text-gray-400 hover:text-blue-600 cursor-pointer"
                              title="Datei herunterladen"
                            >
                              <i className="ri-download-line"></i>
                            </button>
                          </div>
                        </div>
                      )}

                      {password.kontakt_name && (
                        <div className="flex items-center space-x-2 text-sm text-gray-600">
                          <i className="ri-user-line w-4 h-4 flex items-center justify-center"></i>
                          <span>{password.kontakt_name}</span>
                        </div>
                      )}

                      {password.notizen && (
                        <div className="flex items-start space-x-2 text-sm text-gray-600">
                          <i className="ri-sticky-note-line w-4 h-4 flex items-center justify-center mt-0.5"></i>
                          <span className="flex-1">{password.notizen}</span>
                        </div>
                      )}

                      <div className="flex items-center justify-between mt-4">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          {password.kategorie}
                        </span>
                        <span className="text-xs text-gray-500">
                          {new Date(password.created_at).toLocaleDateString('de-DE')}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add/Edit Password Modal */}
      <Modal
        isOpen={isModalOpen || isEditModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setIsEditModalOpen(false);
          resetForm();
          setEditingPassword(null);
        }}
        title={editingPassword ? "Passwort bearbeiten" : "Neues Passwort hinzufügen"}
        maxWidth="lg"
      >
        <form onSubmit={editingPassword ? handleEditSubmit : handleSubmit} className="space-y-4">
          <Input
            label="Titel"
            placeholder="z.B. Gmail Account, Router Admin"
            value={formData.title}
            onChange={(e) => handleInputChange('title', e.target.value)}
            required
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Website/URL"
              placeholder="https://example.com"
              value={formData.website}
              onChange={(e) => handleInputChange('website', e.target.value)}
            />
            <Input
              label="E-Mail"
              type="email"
              placeholder="user@example.com"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
            />
          </div>

          <Input
            label="Benutzername"
            placeholder="Benutzername oder E-Mail"
            value={formData.username}
            onChange={(e) => handleInputChange('username', e.target.value)}
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Passwort
            </label>
            <div className="flex space-x-2">
              <Input
                type="text"
                placeholder="Passwort eingeben"
                value={formData.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                required
                className="flex-1"
              />
              <Button
                type="button"
                variant="secondary"
                onClick={handleGeneratePassword}
                className="whitespace-nowrap"
              >
                <i className="ri-refresh-line mr-1"></i>
                Generieren
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Kategorie"
              options={categories.map(cat => ({ value: cat, label: cat }))}
              value={formData.kategorie}
              onChange={(value) => setFormData(prev => ({ ...prev, kategorie: value as string }))}
              placeholder="Kategorie auswählen"
              required
            />
            <Select
              label="Verknüpfter Kontakt (optional)"
              options={[
                { value: '', label: 'Kein Kontakt' },
                ...contacts.map(contact => ({ 
                  value: contact.id, 
                  label: contact.name + (contact.firma ? ` (${contact.firma})` : '')
                }))
              ]}
              value={formData.kontakt_id}
              onChange={(value) => setFormData(prev => ({ ...prev, kontakt_id: value as string }))}
              placeholder="Kontakt auswählen"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Datei anhängen (optional)
            </label>
            <input
              type="file"
              onChange={handleFileChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.xlsx,.xls,.txt"
            />
            <p className="mt-1 text-xs text-gray-500">
              PDF, Word, Excel, Bilder oder Text-Dateien (max. 50MB)
            </p>
            {editingPassword?.file_name && (
              <p className="mt-1 text-xs text-blue-600">
                Aktuelle Datei: {editingPassword.file_name}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notizen
            </label>
            <textarea
              value={formData.notizen}
              onChange={(e) => setFormData(prev => ({ ...prev, notizen: e.target.value }))}
              placeholder="Zusätzliche Informationen..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
            />
          </div>

          <div className="flex space-x-3 pt-4">
            <Button type="submit" className="flex-1" disabled={uploading}>
              {uploading ? (
                <>
                  <i className="ri-loader-line animate-spin mr-2"></i>
                  {editingPassword ? 'Speichere...' : 'Füge hinzu...'}
                </>
              ) : (
                editingPassword ? 'Änderungen speichern' : 'Passwort hinzufügen'
              )}
            </Button>
            <Button 
              type="button" 
              variant="secondary" 
              onClick={() => {
                setIsModalOpen(false);
                setIsEditModalOpen(false);
                resetForm();
                setEditingPassword(null);
              }}
              disabled={uploading}
            >
              Abbrechen
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
